import { useState, useMemo } from 'react';
import { $, $2, fH, DeptBadge, MarginChip, UtilBar } from './utils';

const SORT_OPTIONS = [
  { value:'revenue_attributed-desc', label:'Revenue (high → low)' },
  { value:'profit_contributed-desc', label:'Profit (high → low)' },
  { value:'profit_contributed-asc',  label:'Profit (low → high)' },
  { value:'total_cost-desc',         label:'Cost (high → low)' },
  { value:'total_hours_worked-desc', label:'Hours (high → low)' },
  { value:'utilization_pct-desc',    label:'Utilization (high → low)' },
  { value:'utilization_pct-asc',     label:'Utilization (low → high)' },
  { value:'hourly_rate-desc',        label:'Hourly rate (high → low)' },
];

export default function TeamTab({ users, overrides }) {
  const [search,  setSearch]  = useState('');
  const [sortKey, setSortKey] = useState('revenue_attributed-desc');
  const [dept,    setDept]    = useState('all');
  const [billFilter, setBillFilter] = useState('all');

  const depts = useMemo(() => ['all', ...new Set(users.map(u => u.department || 'Unassigned').sort())], [users]);

  const overrideMap = useMemo(() => {
    const m = {};
    for (const o of overrides) {
      if (!m[o.user_name]) m[o.user_name] = [];
      m[o.user_name].push(o);
    }
    return m;
  }, [overrides]);

  const filtered = useMemo(() => {
    let data = [...users];
    if (search) data = data.filter(u => u.user_name?.toLowerCase().includes(search.toLowerCase()) || u.position?.toLowerCase().includes(search.toLowerCase()));
    if (dept !== 'all') data = data.filter(u => u.department === dept);
    if (billFilter === 'high') data = data.filter(u => u.utilization_pct >= 80);
    else if (billFilter === 'mid')  data = data.filter(u => u.utilization_pct >= 40 && u.utilization_pct < 80);
    else if (billFilter === 'low')  data = data.filter(u => u.utilization_pct > 0 && u.utilization_pct < 40);
    else if (billFilter === 'zero') data = data.filter(u => u.utilization_pct === 0);
    else if (billFilter === 'fl')   data = data.filter(u => u.is_freelancer);

    const [key, dir] = sortKey.split('-');
    data.sort((a, b) => {
      const av = a[key] ?? (dir==='desc' ? -Infinity : Infinity);
      const bv = b[key] ?? (dir==='desc' ? -Infinity : Infinity);
      return dir === 'desc' ? bv - av : av - bv;
    });
    return data;
  }, [users, search, dept, billFilter, sortKey]);

  const totals = useMemo(() => ({
    th:  filtered.reduce((s,u)=>s+(u.total_hours_worked||0),0),
    tc:  filtered.reduce((s,u)=>s+(u.total_cost||0),0),
    rev: filtered.reduce((s,u)=>s+(u.revenue_attributed||0),0),
    pc:  filtered.reduce((s,u)=>s+(u.profit_contributed||0),0),
  }), [filtered]);

  return (
    <>
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input
          placeholder="Search by name or position…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
        <select value={sortKey} onChange={e=>setSortKey(e.target.value)} style={selectStyle}>
          {SORT_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={dept} onChange={e=>setDept(e.target.value)} style={selectStyle}>
          {depts.map(d=><option key={d} value={d}>{d==='all'?'All Departments':d}</option>)}
        </select>
        <select value={billFilter} onChange={e=>setBillFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Members</option>
          <option value="high">Billable ≥ 80%</option>
          <option value="mid">Billable 40–79%</option>
          <option value="low">Billable &lt; 40%</option>
          <option value="zero">Internal Only (0%)</option>
          <option value="fl">Freelancers Only</option>
        </select>
      </div>

      <div style={tableWrap}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background:'var(--card2)' }}>
              {['#','Name','Position','Department','Hours','Utilization','$/hr','Cost','Revenue','Profit','Margin','Top Clients'].map(h=>(
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u, i) => {
              const hasOv = !!(overrideMap[u.user_name]?.length);
              const clients = Array.isArray(u.by_client) ? u.by_client : (u.by_client ? JSON.parse(u.by_client) : []);
              return (
                <tr key={u.id} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, color:'var(--text3)', fontFamily:'DM Mono,monospace', fontSize:12 }}>{i+1}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                      {u.user_name}
                      {u.is_freelancer && <span style={flTag}>FL</span>}
                      {hasOv && <span title="Has active overrides" style={ovTag}>✎</span>}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, fontSize:12, color:'var(--text2)' }}>{u.position}</td>
                  <td style={tdStyle}><DeptBadge dept={u.department||'Unassigned'} /></td>
                  <td style={monoTd}>{fH(u.total_hours_worked)}</td>
                  <td style={tdStyle}><UtilBar pct={u.utilization_pct||0} /></td>
                  <td style={monoTd}>{$2(u.hourly_rate)}</td>
                  <td style={monoTd}>{$(u.total_cost)}</td>
                  <td style={{ ...monoTd, color:'var(--blue)' }}>{$(u.revenue_attributed)}</td>
                  <td style={{ ...monoTd, fontWeight:600, color: (u.profit_contributed||0)>=0?'var(--green)':'var(--red)' }}>{$(u.profit_contributed)}</td>
                  <td style={tdStyle}><MarginChip value={u.profit_margin} /></td>
                  <td style={tdStyle}>
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                      {clients.slice(0,3).map(c=>(
                        <span key={c.client_name} title={`${c.client_name}: ${$(c.revenue_attributed)} rev`} style={pillStyle}>{c.client_name}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:'2px solid var(--green)', background:'var(--card2)', fontWeight:700 }}>
              <td style={tdStyle}></td>
              <td style={tdStyle}>TOTAL ({filtered.length})</td>
              <td colSpan={2} style={tdStyle}></td>
              <td style={monoTd}>{fH(totals.th)}</td>
              <td colSpan={2} style={tdStyle}></td>
              <td style={monoTd}>{$(totals.tc)}</td>
              <td style={{ ...monoTd, color:'var(--blue)' }}>{$(totals.rev)}</td>
              <td style={{ ...monoTd, color: totals.pc>=0?'var(--green)':'var(--red)' }}>{$(totals.pc)}</td>
              <td style={tdStyle}>
                <MarginChip value={totals.rev>0 ? totals.pc/totals.rev*100 : null} />
              </td>
              <td style={tdStyle}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

const inputStyle = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:8, padding:'9px 14px', color:'var(--text)', fontSize:13, width:240, outline:'none' };
const selectStyle = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:8, padding:'9px 14px', color:'var(--text)', fontSize:13, cursor:'pointer', outline:'none' };
const tableWrap = { overflowX:'auto', borderRadius:12, border:'1px solid var(--border)', background:'var(--card)', boxShadow:'0 1px 3px rgba(0,0,0,.04)' };
const tableStyle = { width:'100%', borderCollapse:'collapse', fontSize:13 };
const thStyle = { padding:'11px 14px', textAlign:'left', fontWeight:600, fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', whiteSpace:'nowrap', position:'sticky', top:0, background:'var(--card2)', zIndex:2 };
const tdStyle = { padding:'9px 14px', whiteSpace:'nowrap', verticalAlign:'middle' };
const monoTd = { ...tdStyle, fontFamily:'DM Mono,monospace', fontSize:12 };
const pillStyle = { display:'inline-block', background:'var(--card2)', border:'1px solid var(--border)', borderRadius:4, padding:'1px 7px', fontSize:11, color:'var(--text2)' };
const flTag = { fontSize:9, background:'var(--purple-bg)', color:'var(--purple)', borderRadius:3, padding:'1px 5px', fontWeight:600 };
const ovTag = { fontSize:10, background:'var(--amber-bg)', color:'var(--amber)', borderRadius:3, padding:'1px 5px', cursor:'default' };
